import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Flower2, Sparkles, Coins, ArrowRight, FileText, ClipboardList, Target, Leaf, Users, Compass, Rss, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/format";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CardGridSkeleton, StatGridSkeleton } from "@/components/Skeletons";

const PRIMARY = [
  { to: "/app/breakout", icon: Activity, key: "breathe" },
  { to: "/app/deep-within", icon: Flower2, key: "reflect" },
  { to: "/app/weva/chat", icon: Sparkles, key: "weva" },
];

const MORE = [
  { to: "/app/goals", icon: Target, key: "goals" },
  { to: "/app/practices", icon: Leaf, key: "practices" },
  { to: "/app/circles", icon: Users, key: "circles" },
  { to: "/coaches", icon: Compass, key: "coaches" },
  { to: "/app/feed", icon: Rss, key: "feed" },
  { to: "/marketplace", icon: Compass, key: "marketplace" },
];

function toArr(r) {
  if (r.status !== "fulfilled") return [];
  const d = r.value?.data;
  return Array.isArray(d) ? d : d?.rows || d?.items || [];
}
function fmtDate(x) {
  if (!x) return "";
  try { return new Date(x).toLocaleDateString(); } catch { return ""; }
}

export default function Dashboard() {
  const t = useT();
  usePageMeta(t("dash.meta_title"));
  const { user } = useAuth();
  const tier = user?.app_subscription?.tier || "free";
  const credits = user?.credits ?? 0;

  const [d, setD] = useState({ journal: [], reports: [], practices: [], pending: [], dw: {} });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.allSettled([
      api.get("/journal?limit=100"),
      api.get("/weva/reports"),
      api.get("/practices/entries?limit=20"),
      api.get("/breathwork/plans/pending"),
      api.get("/deep-within/summary"),
    ]).then((res) => {
      if (!alive) return;
      setD({
        journal: toArr(res[0]),
        reports: toArr(res[1]),
        practices: toArr(res[2]),
        pending: toArr(res[3]),
        dw: res[4].status === "fulfilled" ? (res[4].value?.data || {}) : {},
      });
      setLoaded(true);
    });
    return () => { alive = false; };
  }, []);

  const dwEntries = (d.dw?.totals?.in_progress || 0) + (d.dw?.totals?.transformed || 0);
  const latestReport = d.reports[0];
  const latestPractice = d.practices[0];
  const pendingPlan = d.pending[0];
  const hasContinue = Boolean(pendingPlan || latestReport || latestPractice);

  const stats = [
    { label: t("home.stat_journal"), value: d.journal.length },
    { label: t("home.stat_reports"), value: d.reports.length },
    { label: t("home.stat_practices"), value: d.practices.length },
    { label: t("nav.deep_within"), value: dwEntries },
  ];

  return (
    <div className="space-y-10" data-testid="personal-home">
      {/* 1 — Welcome */}
      <section>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{t("home.welcome_back")}</p>
            <h1 className="font-display text-3xl text-foreground sm:text-4xl">{user?.name || t("dash.hello_fallback")}</h1>
            <p className="mt-2 max-w-md text-muted-foreground">{t("home.welcome_q")}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/app/credits" data-testid="dashboard-buy-credits" className="flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent/50">
              <Coins className="h-4 w-4 text-sage-light" /> {credits}
            </Link>
            <Link to="/app/membership" data-testid="dashboard-upgrade" className="flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent/50">
              <Sparkles className="h-4 w-4 text-sage-light" /> {t(`tier.${tier}`)}
              {tier === "free" && <Badge variant="secondary" className="ml-0.5 rounded-full text-[10px]">{t("dash.upgrade")}</Badge>}
            </Link>
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {PRIMARY.map((a) => (
            <Link
              key={a.key}
              to={a.to}
              data-testid={`home-action-${a.key}`}
              className="group relative overflow-hidden rounded-3xl border border-border bg-card p-6 transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <a.icon className="h-6 w-6" />
              </div>
              <p className="mt-4 font-display text-xl text-foreground">{t(`home.action_${a.key}_title`)}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t(`home.action_${a.key}_sub`)}</p>
              <ArrowRight className="absolute right-5 top-6 h-5 w-5 text-muted-foreground/50 transition-transform group-hover:translate-x-1 group-hover:text-primary" />
            </Link>
          ))}
        </div>
      </section>

      {/* 2 — Continue your journey */}
      <section>
        <h2 className="mb-4 font-display text-xl text-foreground">{t("home.continue_title")}</h2>
        {!loaded ? (
          <CardGridSkeleton count={3} cardClassName="h-28" />
        ) : !hasContinue ? (
          <div className="rounded-3xl border border-dashed border-border bg-card/50 p-8 text-center" data-testid="home-continue-empty">
            <p className="text-sm text-muted-foreground">{t("home.continue_empty")}</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pendingPlan && (
              <ContinueCard to="/app/breathwork/plan" icon={ClipboardList} title={t("home.plan_pending")} sub={pendingPlan.name} cta={t("home.plan_cta")} testId="home-continue-plan" highlight />
            )}
            {latestReport && (
              <ContinueCard to="/app/weva/reports" icon={Sparkles} title={t("home.report_latest")} sub={fmtDate(latestReport.created_at)} cta={t("home.open")} testId="home-continue-report" />
            )}
            {latestPractice && (
              <ContinueCard to="/app/practices/history" icon={Leaf} title={latestPractice.technique_name || latestPractice.name || latestPractice.key || t("nav.practices")} sub={fmtDate(latestPractice.created_at || latestPractice.date)} cta={t("home.open")} testId="home-continue-practice" />
            )}
          </div>
        )}
      </section>

      {/* 3 — Your reflections */}
      <section>
        <h2 className="mb-4 font-display text-xl text-foreground">{t("home.reflections_title")}</h2>
        {!loaded ? (
          <StatGridSkeleton count={4} />
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="home-reflections">
            {stats.map((s) => (
              <div key={s.label} className="rounded-2xl border border-border bg-card p-5">
                <p className="font-display text-3xl font-semibold text-foreground">{s.value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 4 — More to explore */}
      <section>
        <h2 className="mb-3 font-display text-base text-muted-foreground">{t("home.more_title")}</h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {MORE.map((m) => (
            <Link
              key={m.key}
              to={m.to}
              data-testid={`home-more-${m.key}`}
              className="flex items-center gap-3 rounded-2xl border border-border/70 bg-card/60 px-4 py-3 text-sm text-muted-foreground transition-colors hover:bg-accent/40 hover:text-foreground"
            >
              <m.icon className="h-4 w-4 shrink-0" />
              <span className="flex-1 truncate">{t(`nav.${m.key}`)}</span>
              <ChevronRight className="h-4 w-4 shrink-0 opacity-50" />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

function ContinueCard({ to, icon: Icon, title, sub, cta, testId, highlight }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`group flex items-start gap-4 rounded-3xl border p-5 transition-all hover:-translate-y-0.5 hover:shadow-sm ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-card hover:border-primary/40"}`}
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-foreground">{title}</p>
        {sub ? <p className="mt-0.5 truncate text-sm text-muted-foreground">{sub}</p> : null}
        <span className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary">{cta} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
      </div>
    </Link>
  );
}
